import Editor, { DiffEditor, type Monaco, loader } from "@monaco-editor/react";
import * as React from "react";

import { cn } from "@/lib/cn";
import { useTheme } from "@/theme/ThemeProvider";

// Define a Tray-flavoured theme matching the enterprise palette.
// Registered once globally on first mount.
let themesRegistered = false;
function registerThemes(monaco: Monaco) {
  if (themesRegistered) return;
  themesRegistered = true;

  monaco.editor.defineTheme("tray-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "comment", foreground: "6b7280", fontStyle: "italic" },
      { token: "string",  foreground: "1d4ed8" },
      { token: "number",  foreground: "0d9488" },
      { token: "keyword", foreground: "9333ea" },
      { token: "type",    foreground: "92400e" },
    ],
    colors: {
      "editor.background":       "#ffffff",
      "editor.foreground":       "#14171a",
      "editorLineNumber.foreground": "#a3a3a3",
      "editor.lineHighlightBackground": "#fafafa",
      "editorIndentGuide.background": "#f0f0f0",
      "editor.selectionBackground": "#e0e7ff",
      "editor.inactiveSelectionBackground": "#eef2ff",
    },
  });

  monaco.editor.defineTheme("tray-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "9ca3af", fontStyle: "italic" },
      { token: "string",  foreground: "93c5fd" },
      { token: "number",  foreground: "5eead4" },
      { token: "keyword", foreground: "d8b4fe" },
      { token: "type",    foreground: "fcd34d" },
    ],
    colors: {
      "editor.background":       "#16181b",
      "editor.foreground":       "#e6e9ed",
      "editorLineNumber.foreground": "#525252",
      "editor.lineHighlightBackground": "#1c1f23",
      "editorIndentGuide.background": "#222629",
      "editor.selectionBackground": "#1e3a8a",
      "editor.inactiveSelectionBackground": "#1e293b",
    },
  });
}

// Vite-friendly: keep using the default CDN-loader Monaco config.
loader.config({ "vs/nls": { availableLanguages: { "*": "" } } });

// Tune the JavaScript IntelliSense once globally. Every JS editor here edits
// expressions / JS Objects that run in QuickJS (or a `{{ }}` sandbox) — NOT a
// browser DOM — so the default `dom` lib floods completions with irrelevant
// `AbortController`/`window`/… noise (the user's screenshot). Drop `dom`, keep a
// modern ES lib, and re-declare the few globals these editors genuinely have.
let jsDefaultsConfigured = false;
function configureJsDefaults(monaco: Monaco) {
  if (jsDefaultsConfigured) return;
  jsDefaultsConfigured = true;
  const ts = monaco.languages.typescript;
  ts.javascriptDefaults.setCompilerOptions({
    target: ts.ScriptTarget.ES2020,
    lib: ["es2020"], // NO "dom" → no AbortController/window avalanche
    allowNonTsExtensions: true,
    allowJs: true,
    checkJs: false,
  });
  // QuickJS exposes `console`; the `dom` drop would otherwise lose it.
  ts.javascriptDefaults.addExtraLib(
    "declare var console: { log(...a: any[]): void; warn(...a: any[]): void; error(...a: any[]): void };",
    "ts:tray-globals.d.ts",
  );
}

// The TS twin (ADR-0054): the app-logic editor runs in real `typescript` mode so
// the operator gets SEMANTIC type-checking against the ambient `ctx` DTS. Same
// dom-free ES lib as JS. Keep `noLib` false so the ambient DTS's utility types
// (Record/Partial/Extract) resolve.
let tsDefaultsConfigured = false;
function configureTsDefaults(monaco: Monaco) {
  if (tsDefaultsConfigured) return;
  tsDefaultsConfigured = true;
  const ts = monaco.languages.typescript;
  ts.typescriptDefaults.setCompilerOptions({
    target: ts.ScriptTarget.ES2020,
    lib: ["es2020"],
    allowNonTsExtensions: true,
    noEmit: true,
  });
  ts.typescriptDefaults.addExtraLib(
    "declare var console: { log(...a: any[]): void; warn(...a: any[]): void; error(...a: any[]): void };",
    "ts:tray-ts-globals.d.ts",
  );
}


export type EditorLanguage =
  | "yaml"
  | "json"
  | "javascript"
  | "typescript"
  | "python"
  | "html"
  | "css"
  | "plaintext";

export interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  language: EditorLanguage;
  readOnly?: boolean;
  height?: string | number;
  className?: string;
  markers?: { startLine: number; endLine: number; message: string }[];
  /** Ambient TS declarations registered into Monaco's JavaScript defaults
   *  so ``$json.`` etc. get IntelliSense from the node's sample payload
   *  (ADR-0009 #4.3). Only meaningful when ``language === "javascript"``.
   *  Registered under a fixed filename, so a new value REPLACES the prior
   *  one rather than accumulating. */
  jsAmbientTypes?: string;
  /** A SECOND ambient `.d.ts` registered under a DISTINCT filename
   *  (`ts:tray-action-sdk.d.ts`) so it never clobbers / is clobbered by
   *  `jsAmbientTypes` (Monaco's TS service is GLOBAL — a same-filename lib from
   *  another editor instance would overwrite it). Used by the action-JS editor
   *  for the verb SDK (`navigate`/`toast`/`<Query>.run()`), which must coexist
   *  with the shared page-bindings lib. Keep the two libs DISJOINT (no duplicate
   *  `declare const` across them, or TS rejects BOTH). */
  sdkAmbientTypes?: string;
  /** Compact mode for an INLINE editor (a per-field expression / property in an
   *  inspector, not a full code surface): hides the line-number gutter, glyph
   *  margin and folding controls so the editor reads as a one-or-two-line input
   *  rather than a code file. Full editors (node drawer, expand modal, ETL
   *  config) leave this off and keep the gutter. */
  inline?: boolean;
  /** Receive the Monaco editor instance on mount (for imperative ops like
   *  insert-at-cursor). Pairs with `monaco` already captured internally. */
  onEditorMount?: (editor: Parameters<NonNullable<React.ComponentProps<typeof Editor>["onMount"]>>[0]) => void;
  /** Report the count of ERROR-severity markers on this editor's model whenever
   *  they change (ADR-0054 — the app-logic editor blocks the save on TS type
   *  errors). Fires only for `typescript`/`javascript` models. */
  onMarkers?: (errorCount: number) => void;
}

export function CodeEditor({
  value,
  onChange,
  language,
  readOnly = false,
  height = "100%",
  className,
  markers,
  jsAmbientTypes,
  sdkAmbientTypes,
  inline = false,
  onEditorMount,
  onMarkers,
}: CodeEditorProps) {
  const { resolved } = useTheme();
  const monacoRef = React.useRef<Monaco | null>(null);
  const modelOwnerRef = React.useRef<string>("tray-editor");
  // Monaco mounts ASYNCHRONOUSLY (the editor lazy-loads). The ambient-lib
  // effects below run on FIRST render — when `monacoRef.current` is still null —
  // so they bail out; if `jsAmbientTypes`/`sdkAmbientTypes` are stable
  // (memoised), the effect never re-runs and the lib is NEVER registered (the
  // "no IntelliSense in the action editor" bug). Gating the effects on this
  // `ready` flag re-runs them once Monaco is actually up.
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    if (!monacoRef.current) return;
    const monaco = monacoRef.current;
    const models = monaco.editor.getModels();
    for (const model of models) {
      monaco.editor.setModelMarkers(
        model,
        modelOwnerRef.current,
        (markers ?? []).map((m) => ({
          startLineNumber: m.startLine,
          endLineNumber: m.endLine,
          startColumn: 1,
          endColumn: 1000,
          message: m.message,
          severity: 8, // monaco.MarkerSeverity.Error
        })),
      );
    }
  }, [markers]);

  // Feed the binding declarations into Monaco's IntelliSense. Fixed filename →
  // replaces (doesn't stack) on every sample change. For `javascript` it lands on
  // javascriptDefaults; for `typescript` (the app-logic editor) on typescriptDefaults
  // so the ambient `ctx` DTS drives SEMANTIC checking, not just hints.
  React.useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco || !jsAmbientTypes) return;
    const defaults =
      language === "typescript"
        ? monaco.languages.typescript.typescriptDefaults
        : language === "javascript"
          ? monaco.languages.typescript.javascriptDefaults
          : null;
    defaults?.addExtraLib(jsAmbientTypes, "ts:tray-expression-bindings.d.ts");
  }, [jsAmbientTypes, language, ready]);

  // Second ambient lib under a DISTINCT filename (the action-JS verb SDK) so it
  // never clobbers / is clobbered by the shared page-bindings lib above. Keep
  // the two disjoint (no duplicate `declare const` across them).
  React.useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco || language !== "javascript" || !sdkAmbientTypes) return;
    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      sdkAmbientTypes,
      "ts:tray-action-sdk.d.ts",
    );
  }, [sdkAmbientTypes, language, ready]);

  return (
    // `nokey`: ReactFlow's `useKeyPress` attaches a document-level keydown
    // listener and, for keys like Space (panActivationKeyCode) / Backspace /
    // Delete, calls `event.preventDefault()` unless `isInputDOMNode(event)` is
    // true. Monaco's NativeEditContext input is a `<div class="native-edit-context"
    // role="textbox">` — NOT an INPUT/TEXTAREA/`contenteditable`, so ReactFlow
    // doesn't recognise it and would swallow the space (and node-delete keys)
    // while typing in the node editor. `isInputDOMNode` also honours a `.nokey`
    // ancestor, so tagging the editor container is the documented escape hatch.
    <div className={cn("nokey h-full w-full rounded-md border border-border/60 bg-muted/20", className)}>
      <Editor
        value={value}
        defaultLanguage={language}
        language={language}
        height={height}
        theme={resolved === "dark" ? "tray-dark" : "tray-light"}
        beforeMount={(monaco) => {
          monacoRef.current = monaco;
          registerThemes(monaco);
          configureJsDefaults(monaco);
          if (language === "typescript") configureTsDefaults(monaco);
        }}
        onMount={(editor, monaco) => {
          monacoRef.current = monaco;
          setReady(true); // re-run the ambient-lib effects now Monaco is up
          onEditorMount?.(editor);
          if (onMarkers) {
            const report = () => {
              const model = editor.getModel();
              if (!model) return;
              const errs = monaco.editor
                .getModelMarkers({ resource: model.uri })
                .filter((m: { severity: number }) => m.severity === monaco.MarkerSeverity.Error);
              onMarkers(errs.length);
            };
            // Markers arrive asynchronously from the TS worker — re-report on change.
            monaco.editor.onDidChangeMarkers(() => report());
            report();
          }
        }}
        onChange={(v) => onChange?.(v ?? "")}
        options={{
          readOnly,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 13,
          lineHeight: 20,
          fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          fontLigatures: true,
          renderLineHighlight: "all",
          smoothScrolling: true,
          tabSize: 2,
          insertSpaces: true,
          wordWrap: "on",
          padding: { top: 12, bottom: 12 },
          guides: { indentation: true, bracketPairs: false },
          // Relayout when the inspector/dock resizes — a stale layout mispositions
          // the hidden input + overlay widgets (feels like "keys don't land").
          automaticLayout: true,
          // Render suggest/hover/param-hints with position:fixed so they escape the
          // small field container + the inspector's `overflow` (the clipped popover).
          fixedOverflowWidgets: true,
          // Typing a character must NEVER auto-accept a highlighted suggestion —
          // accept is explicit (Enter). Fixes "my keystroke did something weird"
          // while the completion widget is open.
          acceptSuggestionOnCommitCharacter: false,
          tabCompletion: "off",
          scrollbar: {
            verticalScrollbarSize: 10,
            horizontalScrollbarSize: 10,
          },
          // Inline (per-field) editors read as an input, not a code file: drop
          // the line-number gutter, glyph margin and folding controls and pull
          // the text flush to the left. Full editors keep the gutter.
          ...(inline
            ? {
                lineNumbers: "off" as const,
                glyphMargin: false,
                folding: false,
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 0,
              }
            : {}),
        }}
      />
    </div>
  );
}


export interface DiffViewerProps {
  original: string;
  modified: string;
  language: EditorLanguage;
  height?: string | number;
  className?: string;
}

export function DiffViewer({ original, modified, language, height = "100%", className }: DiffViewerProps) {
  const { resolved } = useTheme();
  return (
    // `nokey`: see the main Editor above — keeps ReactFlow's document-level
    // key handling from swallowing Space/Delete while typing in Monaco.
    <div className={cn("nokey h-full w-full rounded-md border border-border/60 bg-muted/20", className)}>
      <DiffEditor
        original={original}
        modified={modified}
        language={language}
        height={height}
        theme={resolved === "dark" ? "tray-dark" : "tray-light"}
        beforeMount={(monaco) => {
          registerThemes(monaco);
        }}
        options={{
          readOnly: true,
          renderSideBySide: true,
          minimap: { enabled: false },
          fontSize: 13,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          scrollBeyondLastLine: false,
          padding: { top: 12 },
        }}
      />
    </div>
  );
}
